import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

// Actions
import { removeTgHeatMapData } from "../../../actions/geo-data/GeoDataActions";
import {
  filterCityRoadStretches,
  getTouchPointsFilteredStretches,
} from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";
import { manageTgAndGetTgOtsForCityStretches } from "../../actions/campaign-planning/CampaignPlanningActions";

// Custom Hooks
import { useAutoManageTargetGroupTab } from "../../utils/HooksUtil";

// Constants and Utils
import { extractZoneIds } from "../../utils/campaignPlanningUtil";
import {
  getCityConfigAllMediaTypes,
  getPlanningQueryParams,
  getSelectedFreqOptLocations,
} from "./CampaignPlanningUtils";

// Tabs
import TargetGroupTab from "./TargetGroupsTab";
import ZonesTab from "./ZonesTab";
import MediaTypesTab from "./MediaTypesTab";
import ArterialRouteTab from "./ArterialRouteTab";
import PointsOfInterestTab from "./PointsOfInterestTab";
import TouchPointsTab from "./TouchPointsTab";
import DataLayersTab from "./DataLayersTab";

// Page Functions
function getTabConstants(
  isTgApplied,
  isZoneApplied,
  isMediaTypeApplied,
  isArterialRouteApplied,
  isPoiSelected,
  isTouchPointsApplied,
  isDataLayersApplied
) {
  // constants
  return [
    {
      isContentApplied: isTgApplied,
      content: (
        <>
          <i className="fas fa-users my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Target Group</p>
        </>
      ),
    },
    {
      isContentApplied: isZoneApplied,
      content: (
        <>
          <i className="fas fa-border-all my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Zone</p>
        </>
      ),
    },
    {
      isContentApplied: isMediaTypeApplied,
      content: (
        <>
          <i className="fas fa-image my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Media Type</p>
        </>
      ),
    },
    {
      isContentApplied: isArterialRouteApplied,
      content: (
        <>
          <i className="fas fa-border-none my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Arterial Route</p>
        </>
      ),
    },
    {
      isContentApplied: isTouchPointsApplied,
      content: (
        <>
          <i className="fas fa-hand-pointer my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Touch Point</p>
        </>
      ),
    },
    {
      isContentApplied: isPoiSelected,
      content: (
        <>
          <i className="fas fa-map-marker-alt my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Points of Interest</p>
        </>
      ),
    },
    {
      isContentApplied: isDataLayersApplied,
      content: (
        <>
          <i className="fas fa-layer-group my-2 fa-2x opacity-75"></i>
          <p className="pb-0">Data Layer</p>
        </>
      ),
    },
  ];
}

// Selected Tab Content
function SelectedContent({
  cityId,
  cityBBox,
  selectedTab,
  setSelectedTab = () => {},
}) {
  const dispatch = useDispatch();

  const appliedFiltersDetailsMap = useSelector(
    (state) => state.planningRoadStretches.appliedFiltersDetailsMap
  );

  // Road Stretches Loading"
  const roadStretchesLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );

  // create and update tg, get heatmap, get tgOts for city stretches
  const manageTgAndGetOtsForCityStretchesLoading = useSelector(
    (state) => state.campaignPlanning.manageTgAndGetOtsForCityStretchesLoading
  );

  // Selected Media types
  const selectedMediaTypesMap = useSelector(
    (state) => state.campaignPlanning.selectedMediaTypes
  );
  const cityConfig = useSelector((state) => state.orgCityConfig.cityConfig);
  const allMediaTypes = getCityConfigAllMediaTypes(cityConfig);
  const selectedMediaTypes = allMediaTypes.filter(
    (mediaType) => selectedMediaTypesMap[mediaType]
  );

  // Selected Route Types
  const selectedArterialRoutesMap = useSelector(
    (state) => state.orgRouteTypes.selectedArterialRoutes
  );
  const selectedArterialRoutes = Object.keys(selectedArterialRoutesMap);

  // Selected Region & Zones Map
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );
  const selectedZoneIds = extractZoneIds(selectedRegionAndZonesMap);
  const selectedFOL = getSelectedFreqOptLocations(
    selectedRegionAndZonesMap,
    cityId
  );

  // selected Touch Points
  const selectedTouchPoints = useSelector(
    (state) => state.campaignPlanning.selectedTouchPoints
  );

  // handle Filter Stretches
  function handleFilterStretches(appliedFrom) {
    dispatch(
      filterCityRoadStretches(
        selectedZoneIds,
        selectedFOL,
        selectedArterialRoutes,
        selectedMediaTypes,
        appliedFrom,
        setSelectedTab
      )
    );
  }

  // handle Generate Heatmap
  function handleGenerateHeatmap(resPoiLayers, tgBean) {
    dispatch(removeTgHeatMapData());

    // create and update tg, get heatmap, get tgOts for city stretches
    dispatch(
      manageTgAndGetTgOtsForCityStretches(
        tgBean,
        resPoiLayers,
        cityId,
        cityBBox,
        setSelectedTab // optional key for closing tab
      )
    );
  }

  function handleFilterTouchPointsStretches() {
    dispatch(
      getTouchPointsFilteredStretches(selectedTouchPoints, setSelectedTab)
    );
  }

  switch (selectedTab) {
    case 1:
      return (
        <TargetGroupTab
          handleTabHeaderBtnClick={handleGenerateHeatmap}
          tabHeaderBtnLoading={manageTgAndGetOtsForCityStretchesLoading}
        />
      );
    case 2:
      return (
        <ZonesTab
          selectedCityId={cityId}
          handleTabHeaderBtnClick={() => handleFilterStretches("zone")}
          tabHeaderBtnLoading={roadStretchesLoading}
          appliedFiltersDetailsMap={appliedFiltersDetailsMap}
        />
      );
    case 3:
      return (
        <MediaTypesTab
          handleTabHeaderBtnClick={() => handleFilterStretches("mediaType")}
          tabHeaderBtnLoading={roadStretchesLoading}
          appliedFiltersDetailsMap={appliedFiltersDetailsMap}
        />
      );
    case 4:
      return (
        <ArterialRouteTab
          handleTabHeaderBtnClick={() => handleFilterStretches("routeType")}
          tabHeaderBtnLoading={roadStretchesLoading}
          appliedFiltersDetailsMap={appliedFiltersDetailsMap}
        />
      );
    case 5:
      return (
        <TouchPointsTab
          handleTabHeaderBtnClick={handleFilterTouchPointsStretches}
          selectedTouchPoints={selectedTouchPoints}
        />
      );
    case 6:
      return <PointsOfInterestTab cityId={cityId} cityBBox={cityBBox} />;
    case 7:
      return <DataLayersTab cityId={cityId} />;

    default:
      return null;
  }
}

/**
 * Planning Map Tabs Section
 */
export default function PlanningMapTabsSection({ cityId, cityBBox }) {
  const { search } = useLocation();
  const { isAddCity } = getPlanningQueryParams(search);

  // State for Opened Tab
  const [selectedTab, setSelectedTab] = useState(0);

  // selected items from tabs contents
  const selectedRegionAndZonesMap = useSelector(
    (state) => state.zonesAndSubZones.selectedRegionAndZonesMap
  );

  // selected map
  const selectedArterialRoutesMap = useSelector(
    (state) => state.orgRouteTypes.selectedArterialRoutes
  );
  const selectedArterialRoutes = Object.keys(selectedArterialRoutesMap);

  const selectedMediaTypesMap = useSelector(
    (state) => state.campaignPlanning.selectedMediaTypes
  );
  const cityConfig = useSelector((state) => state.orgCityConfig.cityConfig);
  const allMediaTypes = getCityConfigAllMediaTypes(cityConfig);
  const selectedMediaTypes = allMediaTypes.filter(
    (type) => selectedMediaTypesMap[type]
  );

  const selectedTgAttributesMap = useSelector(
    (state) => state.campaignPlanning.selectedTgAttributesMap
  );
  const brandIds = useSelector((state) => state.poiSelection.brandIds) || {};

  const selectedTouchPoints = useSelector(
    (state) => state.campaignPlanning.selectedTouchPoints
  );

  const dataLayerSelectedMap = useSelector(
    (state) => state.dataLayersSelection.isLayerSelected
  );

  const isTgApplied = Object.keys(selectedTgAttributesMap).length > 0;
  const isZoneApplied = Object.keys(selectedRegionAndZonesMap).length > 0;
  const isMediaTypeApplied = selectedMediaTypes.length > 0;
  const isArterialRouteApplied = selectedArterialRoutes.length > 0;
  const isPoiSelected = Object.keys(brandIds).length > 0;
  const isTouchPointsApplied = selectedTouchPoints.length > 0;
  const isDataLayersApplied = Object.keys(dataLayerSelectedMap).length > 0;

  // tab constants
  const tabConstants = getTabConstants(
    isTgApplied,
    isZoneApplied,
    isMediaTypeApplied,
    isArterialRouteApplied,
    isPoiSelected,
    isTouchPointsApplied,
    isDataLayersApplied
  );

  // "ADD CITY" to campaign
  useAutoManageTargetGroupTab(isAddCity);

  // tab click
  function tabOnClick(index) {
    const isSameTabClicked = selectedTab === index;
    const newTabIndex = isSameTabClicked ? 0 : index;

    // update state
    setSelectedTab(newTabIndex);
  }

  return (
    <div className="toohl-map-tabs-container">
      <div className="tab-items">
        {tabConstants.map((eachTab, index) => {
          const { isContentApplied = false, content } = eachTab;
          const tabClassName = isContentApplied
            ? "text-success"
            : "text-primary";
          return (
            <div
              key={index}
              className={`tab-item ${tabClassName} ${
                selectedTab === index + 1 ? "active" : ""
              }`}
              onClick={() => tabOnClick(index + 1)}
              disabled={!cityId}
            >
              {content}
            </div>
          );
        })}
      </div>
      {!!selectedTab && (
        <div className="tab-cont bg-white">
          <SelectedContent
            cityId={cityId}
            cityBBox={cityBBox}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
        </div>
      )}
    </div>
  );
}
